import { useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { message } from 'antd';
import {
  HierarchyNodesIcon,
  CrTransferIcon,
  CheckmarkIcon,
  SearchIcon,
  CaretIcon,
  FileXlsxIcon,
  UploadIcon,
} from '../../assets/icons';
import { Spinner } from '../../components/ui';
import LevelHierarchyEditor, {
  buildTree,
  type Level,
  type LevelNode,
} from './createOrg/LevelHierarchyEditor';
import DataImportStep from './createOrg/DataImportStep';
import {
  ConstituencyTree,
  type HierarchyView,
  type SavedArea,
} from './createOrg/DefineBoundaryStep';
import {
  useGetLevelsQuery,
  useGetEcScopesQuery,
  useCreateLevelsBulkMutation,
  type OrgNode,
} from '../../features/organizations/organizationsApi';
import { useActiveOrgId } from '../../features/organizations/useActiveOrg';

/**
 * Seed levels for the Union Government hierarchy (flat parent-linked list, mirrors the Figma
 * design). Shared by the "Build" editor and the read-only "Preview".
 */
/** Preset hierarchy per Build-Hierarchy submenu (static demo data where there's no API yet). */
const SEED_BY_LABEL: Record<string, Level[]> = {
  'State Police': [
    { id: 'sp-hq', name: 'State Police Headquarters', parentId: null },
    { id: 'sp-range', name: 'Range', parentId: 'sp-hq' },
    { id: 'sp-dist', name: 'District Police', parentId: 'sp-range' },
    { id: 'sp-sdpo', name: 'Sub-Division (SDPO)', parentId: 'sp-dist' },
    { id: 'sp-ps', name: 'Police Station', parentId: 'sp-sdpo' },
    { id: 'sp-beat', name: 'Beat', parentId: 'sp-ps' },
  ],
};

/* ------------------------------------------------------------------ *
 * State Police — static demo data for the mapping preview (no API yet).
 * ------------------------------------------------------------------ */

/** SOURCE panel: a sample internal police hierarchy (with a booth area to map). */
const SP_SOURCE_TREE: OrgNode[] = [
  {
    id: 1,
    org_name: 'State Police Headquarters',
    level_name: 'HQ',
    areas: [],
    children: [
      {
        id: 2,
        org_name: 'Jaipur Range',
        level_name: 'Range',
        areas: [],
        children: [
          {
            id: 3,
            org_name: 'Alwar District Police',
            level_name: 'District',
            areas: [],
            children: [
              {
                id: 4,
                org_name: 'Alwar Rural Police Station',
                level_name: 'Police Station',
                children: [],
                areas: [
                  { id: 101, area_code: 'S20_03_065_001', booth_name: 'Beat 1 — Bahadarpur' },
                  { id: 102, area_code: 'S20_03_065_002', booth_name: 'Beat 2 — Naugaon' },
                ],
              },
            ],
          },
        ],
      },
    ],
  },
];

// DESTINATION panel: a sample Election Commission structure (State → … → Booth). No node is
// marked `selected`, so DataImportStep keeps this full static subtree instead of lazily fetching
// levels from the territory API. The outer node is the root wrapper (its children are the states).
const SP_EC_SELECTION: HierarchyView[] = [
  {
    id: 'sp-root',
    label: 'Election Commission',
    selected: false,
    children: [
      {
        id: 'sp-state',
        label: 'Rajasthan',
        kind: 'state',
        code: 'S20',
        selected: false,
        children: [
          {
            id: 'sp-pc',
            label: 'Alwar',
            kind: 'parliament',
            code: '3',
            selected: false,
            children: [
              {
                id: 'sp-ac',
                label: 'Alwar Rural',
                kind: 'assembly',
                code: 'S20_03_065',
                areaCode: 'S20_03_065',
                selected: false,
                children: [
                  {
                    id: 'sp-branch',
                    label: 'Rural',
                    kind: 'branch',
                    selected: false,
                    children: [
                      {
                        id: 'sp-gram',
                        label: 'Bahadarpur',
                        kind: 'gram',
                        code: '72389',
                        areaCode: '72389',
                        selected: false,
                        children: [
                          {
                            id: 'sp-village',
                            label: 'Bahadarpur',
                            kind: 'village',
                            code: '72389',
                            areaCode: '72389',
                            selected: false,
                            children: [
                              {
                                id: 'sp-booth-1',
                                label: 'Beat 1 — Bahadarpur · S20_03_065_001',
                                kind: 'booth',
                                code: 'S20_03_065_001',
                                areaCode: 'S20_03_065_001',
                                selected: false,
                                children: [],
                              },
                              {
                                id: 'sp-booth-2',
                                label: 'Beat 2 — Naugaon · S20_03_065_002',
                                kind: 'booth',
                                code: 'S20_03_065_002',
                                areaCode: 'S20_03_065_002',
                                selected: false,
                                children: [],
                              },
                            ],
                          },
                        ],
                      },
                    ],
                  },
                ],
              },
            ],
          },
        ],
      },
    ],
  },
];

type Step = 'build' | 'preview';

/** Keep matching nodes plus their ancestor chain so the tree stays navigable while searching. */
function filterLevels(levels: Level[], query: string): Level[] {
  const q = query.trim().toLowerCase();
  if (!q) return levels;
  const byId = new Map(levels.map((l) => [l.id, l]));
  const keep = new Set<string>();
  levels.forEach((l) => {
    if (!l.name.toLowerCase().includes(q)) return;
    let cur: Level | undefined = l;
    while (cur) {
      keep.add(cur.id);
      cur = cur.parentId ? byId.get(cur.parentId) : undefined;
    }
  });
  return levels.filter((l) => keep.has(l.id));
}

const STEP_ACTIVE_RING =
  'shadow-[0px_0px_0px_4px_rgba(37,99,235,0.2),0px_4px_6px_-1px_rgba(0,74,198,0.2),0px_2px_4px_-2px_rgba(0,74,198,0.2)]';

/** Top panel — horizontal stepper for the build → preview flow (both steps clickable). */
function StepperPanel({
  step,
  onChange,
  label,
}: {
  step: Step;
  onChange: (s: Step) => void;
  label: string;
}) {
  const buildDone = step === 'preview';
  const steps: { key: Step; title: string; Icon: typeof HierarchyNodesIcon }[] = [
    { key: 'build', title: `Build ${label} Hierarchy`, Icon: HierarchyNodesIcon },
    { key: 'preview', title: `${label} Hierarchy Preview`, Icon: CrTransferIcon },
  ];
  return (
    <div className="overflow-x-auto rounded-xl border border-line-soft bg-white p-6 shadow-card">
      <div className="relative mx-auto flex min-w-[420px] max-w-[640px] items-start justify-between px-2">
        {/* Connector line behind the circles */}
        <div
          className={[
            'absolute left-[70px] right-[70px] top-7 h-0.5 rounded-full',
            buildDone ? 'bg-brand' : 'bg-[#d1e2ff]',
          ].join(' ')}
        />
        {steps.map(({ key, title, Icon }, i) => {
          const active = step === key;
          const completed = i < steps.findIndex((s) => s.key === step);
          return (
            <button
              key={key}
              type="button"
              onClick={() => onChange(key)}
              className="relative z-[1] flex w-[160px] flex-col items-center gap-3"
            >
              <span
                className={[
                  'flex size-14 items-center justify-center rounded-full',
                  active
                    ? `bg-brand text-white ${STEP_ACTIVE_RING}`
                    : completed
                      ? 'bg-brand text-white'
                      : 'bg-[#d1e2ff] text-brand',
                ].join(' ')}
              >
                {completed ? <CheckmarkIcon className="size-6" /> : <Icon className="size-6" />}
              </span>
              <span className="text-center text-sm font-medium leading-5 text-ink">{title}</span>
              <span className="text-center text-[13px] font-medium leading-5 text-ink-secondary">
                {completed ? 'Completed' : active ? 'In Progress' : 'Pending'}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

/** One node in the read-only preview tree — nested cards with a connector rail. */
function PreviewNode({ node, depth }: { node: LevelNode; depth: number }) {
  const [open, setOpen] = useState(true);
  const hasChildren = node.children.length > 0;
  return (
    <div className="w-full">
      <div className="flex items-center gap-2 py-0.5">
        {hasChildren ? (
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-label={open ? 'Collapse' : 'Expand'}
            className="flex size-6 shrink-0 items-center justify-center rounded text-ink-secondary transition-colors hover:bg-brand-shade"
          >
            <CaretIcon className={['size-3.5 transition-transform', open ? '' : '-rotate-90'].join(' ')} />
          </button>
        ) : (
          <span className="size-6 shrink-0" />
        )}
        <span className={['text-sm text-ink', depth === 0 ? 'font-bold' : 'font-semibold'].join(' ')}>
          {node.name}
        </span>
      </div>

      {hasChildren && open && (
        <div className="ml-3 mt-2 flex flex-col gap-2 rounded-lg border-l-[1.5px] border-line-soft bg-white p-2.5 pl-4">
          {node.children.map((child) => (
            <PreviewNode key={child.id} node={child} depth={depth + 1} />
          ))}
        </div>
      )}
    </div>
  );
}

/** A quick-action card above the preview tree (Download template / Bulk upload). */
function ActionCard({
  icon,
  iconBg,
  title,
  subtitle,
  action,
}: {
  icon: React.ReactNode;
  iconBg: string;
  title: string;
  subtitle: string;
  action: React.ReactNode;
}) {
  return (
    <div className="flex flex-1 items-center justify-between gap-4 rounded-xl border border-line bg-white p-4 shadow-card">
      <div className="flex items-center gap-2.5">
        <span className={['flex size-[30px] items-center justify-center rounded-[15px]', iconBg].join(' ')}>
          {icon}
        </span>
        <div className="flex flex-col">
          <span className="text-sm font-semibold leading-6 text-ink">{title}</span>
          <span className="text-xs leading-[18px] text-ink-700">{subtitle}</span>
        </div>
      </div>
      {action}
    </div>
  );
}

/** Read-only preview of the hierarchy + template / bulk-upload actions. */
function HierarchyPreview({
  levels,
  label,
  onDownload,
  onUpload,
  downloading,
  uploading,
}: {
  levels: Level[];
  label: string;
  /** When provided, wires the template download to the backend (else a stub message). */
  onDownload?: () => void;
  /** When provided, wires bulk upload to the backend (else a stub message). */
  onUpload?: (file: File) => void;
  downloading?: boolean;
  uploading?: boolean;
}) {
  const [search, setSearch] = useState('');
  const roots = useMemo(() => buildTree(filterLevels(levels, search)), [levels, search]);
  const fileRef = useRef<HTMLInputElement>(null);

  return (
    <div className="flex min-w-0 flex-1 flex-col gap-4">
      <input
        ref={fileRef}
        type="file"
        accept=".xlsx,.xls,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        className="hidden"
        onChange={(e) => {
          const f = e.currentTarget.files?.[0];
          if (f) onUpload?.(f);
          e.currentTarget.value = '';
        }}
      />
      {/* Template + bulk-upload cards */}
      <div className="flex flex-col gap-4 sm:flex-row">
        <ActionCard
          icon={<FileXlsxIcon className="size-4 text-brand" />}
          iconBg="bg-[#cdd4ff]"
          title="Hierarchy Template"
          subtitle="Download the structure"
          action={
            <button
              type="button"
              disabled={downloading}
              onClick={() =>
                onDownload
                  ? onDownload()
                  : message.info('Template download will be available once the API is ready.')
              }
              className="shrink-0 rounded-lg bg-[#dce3ec] px-4 py-1.5 text-xs font-semibold tracking-[0.24px] text-[#5e656d] transition-colors hover:bg-[#cfd8e3] disabled:opacity-60"
            >
              {downloading ? 'Preparing…' : 'Download XLSX'}
            </button>
          }
        />
        <ActionCard
          icon={<UploadIcon className="size-4 text-[#f97316]" />}
          iconBg="bg-[#ffdbcd]"
          title="Bulk Upload Hierarchy"
          subtitle="Import multiple Admin levels"
          action={
            <button
              type="button"
              disabled={uploading}
              onClick={() =>
                onUpload
                  ? fileRef.current?.click()
                  : message.info('Bulk upload will be available once the API is ready.')
              }
              className="shrink-0 rounded-lg bg-brand px-4 py-1.5 text-xs font-semibold tracking-[0.24px] text-white transition-colors hover:bg-brand-dark disabled:opacity-60"
            >
              {uploading ? 'Uploading…' : 'Select Files'}
            </button>
          }
        />
      </div>

      {/* Preview card */}
      <div className="flex flex-col overflow-hidden rounded-xl border border-line-soft bg-white shadow-card">
        <div className="flex items-center justify-between border-b border-[#f1f5f9] px-4 py-4">
          <div className="flex items-center gap-2">
            <span className="rounded bg-[#f1f5f9] px-2 py-0.5 text-[11px] font-bold uppercase tracking-[0.55px] text-[#64748b]">
              Source
            </span>
            <span className="text-sm font-semibold text-[#1e293b]">{label} Hierarchy Preview</span>
          </div>
          <span className="text-xs font-medium text-[#64748b]">Updated just now</span>
        </div>

        <div className="flex flex-col gap-5 p-5">
          <div className="relative">
            <SearchIcon className="pointer-events-none absolute left-3.5 top-1/2 size-4 -translate-y-1/2 text-[#94a3b8]" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search administrative units, posts, or ministries..."
              className="w-full rounded-lg border border-line-soft bg-white py-2.5 pl-9 pr-4 text-sm text-ink placeholder:text-[#94a3b8] focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/15"
            />
          </div>

          <div className="min-h-[300px] rounded-lg bg-[#f8fafc]/60 p-2.5">
            {roots.length > 0 ? (
              roots.map((node) => <PreviewNode key={node.id} node={node} depth={0} />)
            ) : (
              <p className="py-10 text-center text-sm text-ink-secondary">No matching units.</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Build Hierarchy page — one design shared by every Build-Hierarchy submenu; only the `label`
 * changes. When `apiKind` is set (e.g. "admin" for State Revenue) the add-levels / template
 * download / bulk upload wire to the org's real hierarchy APIs (scoped to the signed-in admin's
 * organization); otherwise they're front-end stubs.
 */
export default function BuildHierarchyPage({
  label,
  apiKind,
  kind,
  staticMapping = false,
}: {
  label: string;
  /**
   * Enables the real hierarchy APIs. 'org' = organization levels (no `kind`); 'admin' and
   * 'police' pass that value as the backend `kind`.
   */
  apiKind?: 'org' | 'admin' | 'police';
  /** Explicit backend `kind` (e.g. per-department, "dept-5") — enables the API and overrides apiKind. */
  kind?: string;
  /** Render the mapping preview with static demo data (no API) — e.g. a page without a kind. */
  staticMapping?: boolean;
}) {
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>('build');

  // Real hierarchy wiring: scope to the signed-in admin's organization. `kindParam` is the
  // backend `kind` (undefined for the org hierarchy; 'admin'/'police'/'dept-<id>' otherwise).
  const apiEnabled = apiKind != null || kind != null;
  // Both the API pages and the static demo page show the mapping preview.
  const showMapping = apiEnabled || staticMapping;
  const kindParam = kind ?? (apiKind && apiKind !== 'org' ? apiKind : undefined);
  // Scope to the header-selected org (falls back to the signed-in admin's org).
  const organization = useActiveOrgId();
  const { data: savedLevels, isFetching: levelsFetching } = useGetLevelsQuery(
    { organization: organization as number, kind: kindParam },
    { skip: !apiEnabled || organization == null },
  );
  // Loading the org's saved levels (prefill) — show a loader over the editor meanwhile.
  const hierarchyLoading = apiEnabled && levelsFetching;
  // Prefill the editor from the org's saved levels (edit existing hierarchy).
  const prefill = useMemo<Level[]>(
    () =>
      (savedLevels ?? []).map((l) => ({
        id: String(l.id),
        name: l.level_name,
        parentId: l.parent != null ? String(l.parent) : null,
      })),
    [savedLevels],
  );
  const seed = apiEnabled ? prefill : (SEED_BY_LABEL[label] ?? []);

  // The org's saved EC boundaries → the selection that feeds the mapping's Election Commission
  // Structure. A headless ConstituencyTree (below) reveals these and reports the selection,
  // exactly as the org wizard does between step 2 and the mapping step.
  const { data: savedScopes } = useGetEcScopesQuery(
    { organization: organization as number },
    { skip: !apiEnabled || organization == null },
  );
  const preselectedAreas = useMemo<SavedArea[]>(
    () =>
      (savedScopes ?? []).map((s) => ({
        levelRank: s.ec_location_level_id,
        areaCode: s.area_code,
      })),
    [savedScopes],
  );
  const [selection, setSelection] = useState<HierarchyView[]>([]);

  const [createLevelsBulk, { isLoading: saving }] = useCreateLevelsBulkMutation();

  // Levels live here so the Build editor and the Preview stay in sync.
  const [levels, setLevels] = useState<Level[]>(SEED_BY_LABEL[label] ?? []);

  // Save the level definitions (create/replace). Returns whether it succeeded.
  const handleSave = async (): Promise<boolean> => {
    if (!apiEnabled) {
      message.success('Hierarchy saved.');
      return true;
    }
    if (organization == null) {
      message.error('No organization is linked to your account.');
      return false;
    }
    try {
      await createLevelsBulk({
        organization,
        replace: true,
        kind: kindParam,
        levels: levels.map((l) => ({ id: l.id, name: l.name, parentId: l.parentId })),
      }).unwrap();
      message.success(`${label} hierarchy saved.`);
      return true;
    } catch {
      message.error('Could not save the hierarchy. Please try again.');
      return false;
    }
  };

  // Build step: save the levels, then open the mapping preview (which needs saved levels
  // for its template download / bulk upload).
  const saveThenPreview = async () => {
    if (await handleSave()) setStep('preview');
  };

  // Stepper click: going to the preview auto-saves the latest step-1 hierarchy first, so the
  // mapping/source tree always reflects the current levels. Going back to build just switches.
  const handleStepChange = (s: Step) => {
    if (s === 'build') {
      setStep('build');
    } else if (apiEnabled) {
      void saveThenPreview();
    } else {
      setStep('preview');
    }
  };

  return (
    <div className="flex flex-col gap-6">
      {/* Breadcrumb + title */}
      <div className="flex flex-col gap-1">
        <nav className="flex items-center gap-2 text-xs font-medium text-ink-secondary">
          <span>Configuration</span>
          <span>›</span>
          <span>Administrative Levels</span>
          <span>›</span>
          <span className="text-brand">{label}</span>
        </nav>
        <h1 className="pt-1 text-2xl font-semibold leading-8 text-ink">
          Administrative Hierarchy Configuration
        </h1>
        <p className="text-sm leading-5 text-ink-secondary">
          Configure and sequence the {label} hierarchy of your organization.
        </p>
      </div>

      {/* Horizontal stepper on top, then the build editor / preview below. The editor stays
          mounted (just hidden in preview) so in-progress edits aren't lost when switching steps. */}
      <StepperPanel step={step} onChange={handleStepChange} label={label} />
      <div className="flex flex-col gap-6">
        <div className={['min-w-0 flex-1', step === 'build' ? '' : 'hidden'].join(' ')}>
          {hierarchyLoading ? (
            <div className="flex h-64 items-center justify-center rounded-xl border border-line-soft bg-white shadow-card">
              <Spinner size={24} />
            </div>
          ) : (
            <LevelHierarchyEditor
              title={`${label} Hierarchy`}
              initialLevels={seed}
              onLevelsDataChange={setLevels}
            />
          )}
        </div>
        {/* Preview: the EC-mapping screen (internal hierarchy with counts + link / unlink).
            API pages use live data; the static page uses demo data; others show the stub. */}
        {step === 'preview' &&
          (showMapping ? (
            <div className="min-w-0 flex-1">
              {/* Headless boundary tree — reveals the org's saved EC boundaries and reports the
                  selection so the mapping's Election Commission Structure populates (API pages). */}
              {apiEnabled && (
                <div className="hidden">
                  <ConstituencyTree
                    active
                    preselectedAreas={preselectedAreas}
                    onSelectionChange={setSelection}
                  />
                </div>
              )}
              <DataImportStep
                active
                kind={kindParam}
                organizationId={apiEnabled ? (organization ?? null) : null}
                selection={apiEnabled ? selection : SP_EC_SELECTION}
                staticSourceTree={staticMapping ? SP_SOURCE_TREE : undefined}
                onConfirm={() => navigate('/admin/organizations')}
                hierarchyLevels={levels.map((l) => l.name)}
                sourceTitle={
                  apiKind === 'admin'
                    ? 'Administrative Hierarchy'
                    : apiKind === 'police'
                      ? 'Police Hierarchy'
                      : 'Internal Organization'
                }
                bulkSubtitle={
                  apiKind === 'admin'
                    ? 'Import multiple administrative levels'
                    : apiKind === 'police'
                      ? 'Import multiple police levels'
                      : 'Import multiple organization levels'
                }
              />
            </div>
          ) : (
            <HierarchyPreview levels={levels} label={label} />
          ))}
      </div>

      {/* Actions */}
      <div className="flex items-center justify-end gap-4">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="h-10 rounded-lg border border-line px-8 text-sm font-semibold text-ink-700 transition-colors hover:bg-brand-shade"
        >
          Cancel
        </button>
        {step === 'build' ? (
          <button
            type="button"
            disabled={saving}
            onClick={apiEnabled ? saveThenPreview : () => setStep('preview')}
            className="h-10 rounded-lg bg-brand px-10 text-sm font-semibold text-white shadow-btn transition-colors hover:bg-brand-dark disabled:opacity-60"
          >
            {saving ? 'Saving…' : apiEnabled ? 'Save & Continue' : 'Preview'}
          </button>
        ) : (
          <button
            type="button"
            disabled={saving}
            onClick={handleSave}
            className="h-10 rounded-lg bg-brand px-10 text-sm font-semibold text-white shadow-btn transition-colors hover:bg-brand-dark disabled:opacity-60"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        )}
      </div>
    </div>
  );
}
